import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, Event, NavigationEnd } from '@angular/router';
import { Subscription, of, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthenticationService, AddressesService, DialogService } from '@box-core/services';
import { Address, Timeslot, User } from '@box-types';
import { TimeslotDialogComponent } from '@box-shared/components';
import { TimeslotsService } from '@box-core/services/timeslots.service';
import { TimeslotDialogResponse } from '@box-shared/components/timeslot-dialog/timeslot-dialog-response.interface';
import { UserService } from '@box-core/services/user.service';
import { GlobalStateService } from '@box-core/services/global-state.service';
import { BoxHeaderDeliveryDetailsComponent } from '@box-react/components';
import { isSeoLocationPage } from '@box/utils';
import { LocationsService } from '@box-delivery/services';

@Component({
  selector: 'box-header',
  templateUrl: './box-header.component.html',
  styleUrls: ['./box-header.component.scss']
})
export class BoxHeaderComponent implements OnInit, OnDestroy {
  private disabledRoutes = ['/checkout', '/login', '/logout', '/payment'];
  private userSubscription: Subscription;
  private addressSubscription: Subscription;
  private addressesSubscription: Subscription;
  private timeslotSubscription: Subscription;
  private routerSubscription: Subscription;
  private breakPointObserverSubscription: Subscription;

  public user: User;
  public address: Address;
  public addresses: Address[];
  public timeslot: Timeslot;
  public showDeliveryDetails = false;
  public authenticated: boolean;
  public isGuest: boolean;
  public isSeoLocationPage: boolean;
  public isHomePage: boolean = false;

  public boxHeaderDeliveryDetailsComponent$: Observable<typeof BoxHeaderDeliveryDetailsComponent> = of(
    BoxHeaderDeliveryDetailsComponent
  );
  public onAddressChangeCallBack;
  public onTimeslotChangeCallback;

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private addressesService: AddressesService,
    private dialogService: DialogService,
    private timeslotsService: TimeslotsService,
    private userServices: UserService,
    private globalStateService: GlobalStateService,
    private locationsService: LocationsService
  ) {
    // TODO Find a clean way to detect the activation of the delivery details
    this.setRouterSubscription();
    this.setAddressSubscription();
    this.setAddressesSubscription();
    this.setTimeslotSubscription();
    this.onAddressChangeCallBack = this.onAddressChange.bind(this);
    this.onTimeslotChangeCallback = this.onTimeslotChange.bind(this);
  }

  ngOnInit(): void {
    this.isSeoLocationPage = this.locationsService.isSeoLocationPage();
    this.authenticated = this.authenticationService.isAuthenticated;
    this.isGuest = this.userServices.isGuest;
  }

  ngOnDestroy(): void {
    this.clearSubscriptions();
  }

  private setAddressesSubscription(): void {
    this.addressesSubscription = this.globalStateService.addresses$.subscribe(
      (addresses) => (this.addresses = addresses)
    );
  }

  private setAddressSubscription(): void {
    this.addressSubscription = this.globalStateService.address$.subscribe((address) => (this.address = address));
  }

  private setTimeslotSubscription(): void {
    this.timeslotSubscription = this.timeslotsService.timeslot$.subscribe((timeslot) => (this.timeslot = timeslot));
  }

  private setRouterSubscription(): void {
    this.routerSubscription = this.router.events
      .pipe(filter((event: Event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const routerUrl: string = this.router.url.split('?')[0].split('#')[0];
        this.showDeliveryDetails = !this.disabledRoutes.includes(routerUrl);
        this.isHomePage = event.urlAfterRedirects === '/home';
      });
  }

  private clearSubscriptions(): void {
    this.userSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    this.addressSubscription?.unsubscribe();
    this.timeslotSubscription?.unsubscribe();
    this.addressesSubscription?.unsubscribe();
    this.breakPointObserverSubscription?.unsubscribe();
  }

  private openTimeslotsDialog(): void {
    this.dialogService
      .openDialog(TimeslotDialogComponent, {
        panelClass: 'box-dialog',
        ariaLabel: 'when_do_you_want_to_receive'
      })
      .afterClosed()
      .subscribe((data: TimeslotDialogResponse) => {
        if (!data) return;
        if (data.reset) this.timeslotsService.clearTimeslot();
        if (data.timeslot) this.timeslotsService.setTimeslot(data.timeslot);
      });
  }

  public onAddressChange(): void {
    if (isSeoLocationPage(this.router.url)) return void this.router.navigate(['/home']);

    if (this.authenticated) {
      return void this.addressesService.initiateAddressSelectDialogFlow$().subscribe();
    }
    if (this.address) {
      return void this.addressesService.initiateEditAddressDialogFlow$(false, this.address).subscribe();
    }
    return void this.addressesService.initiateAddSmallAddressDialogFlow$().subscribe();
  }

  public onTimeslotChange(): void {
    this.openTimeslotsDialog();
  }
}
